import { BundleTypes, DISCOUNT_TYPES } from "../../constants";
import { buildResourcePickerQuery } from "../../../constants";
import { useEffect } from "react";
import { CollectionsType, ProductObj, ReviewStepType } from "../../Types";
import { Text, BlockStack, InlineStack, Box, Tooltip, Icon } from "@shopify/polaris";
import RHFRenderSelectedProduct from "../../../../components/RHFRenderSelectedProduct/RHFRenderSelectedProduct";
import RHFRenderSelectedCollections from "../../../../components/RHFRenderSelectedCollections/RHFRenderSelectedCollections";
import { InfoIcon } from "@shopify/polaris-icons";
import { getNumberFieldSuffix } from "../../../../utilities/constants";
import { useFormContext, useWatch } from "react-hook-form";
import { FormSelect, FormTextField, FormToggle } from "../../../../components/FormFields";

const ReviewAddOnProducts = ({ money_format }: { money_format: string }) => {
    const { control, setValue, trigger, formState } = useFormContext();
    const reviewStep = useWatch({ control, name: "review_step" }) as ReviewStepType;
    const reviewStepFieldErrors = (formState.errors as any)?.review_step as Record<string, any> | undefined;

    const handleEditReviewStep = (key: keyof ReviewStepType, value: any) => {
        setValue(`review_step.${key}` as any, value, { shouldDirty: true });
    };

    // Handle Product Selection Resetting
    useEffect(() => {
        const selection = reviewStep?.addon_product_selection;

        if (!reviewStep?.enable_addon_products) return;

        if (selection === "specific") {
            // Clear collections when switching to specific
            if (reviewStep?.collections?.length) handleEditReviewStep("collections", []);
        }

        if (selection === "collection") {
            // Clear products when switching to collection
            if (reviewStep?.addon_products?.length) handleEditReviewStep("addon_products", []);
        }
    }, [reviewStep?.addon_product_selection]);

    const handleTitleChange = (value: string) => {
        handleEditReviewStep("addon_section_title", value);
        trigger("review_step");
    };

    const handleProductsChange = (value: ProductObj[]) => {
        handleEditReviewStep("addon_products", value);
        trigger("review_step");
    };

    const handleCollectionsChange = (value: CollectionsType[]) => {
        handleEditReviewStep("collections", value);
        trigger("review_step");
    };

    return (
        <Box paddingInline="400">
            {/* <Card padding="400" background="bg-surface-secondary"> */}
            <BlockStack gap="400">
                {/* Header & Toggle */}
                <BlockStack gap="200">
                    <InlineStack gap="200" blockAlign="center">
                        <Text variant="headingSm" as="h3">
                            Add-on products (optional)
                        </Text>
                        <Tooltip content="Show additional products on the review step that customers can add before checkout.">
                            <Icon source={InfoIcon} tone="primary" />
                        </Tooltip>
                    </InlineStack>
                    <Text as="p" tone="subdued">
                        Show additional products on the review step that customers can add before checkout.
                    </Text>
                </BlockStack>

                <InlineStack align="space-between" blockAlign="center">
                    <Text as="span" variant="bodyMd" fontWeight="medium">
                        Enable add-on products
                    </Text>
                    <FormToggle
                        name="review_step.enable_addon_products"
                        checked={!!reviewStep?.enable_addon_products}
                        onChange={(val) => handleEditReviewStep("enable_addon_products", val)}
                    />
                </InlineStack>

                {reviewStep?.enable_addon_products && (
                    <BlockStack gap="400">
                        <FormTextField
                            name="review_step.addon_section_title"
                            label="Add-on section title"
                            value={reviewStep?.addon_section_title}
                            onChange={handleTitleChange}
                            placeholder="Complete your box"
                            maxLength={100}
                            showCharacterCount
                        />

                        <FormSelect
                            name="review_step.addon_product_selection"
                            label="Choose add-on products"
                            value={reviewStep?.addon_product_selection}
                            onChange={(val) => handleEditReviewStep("addon_product_selection", val)}
                            options={[
                                { label: "Specific products", value: "specific" },
                                { label: "Collection", value: "collection" },
                            ]}
                        />

                        {/* Specific Product selection */}
                        {reviewStep?.addon_product_selection === "specific" && (
                            <div style={{ display: "flex", flexDirection: "column", marginBottom: 10 }}>
                                <RHFRenderSelectedProduct
                                    bundleLevel="product"
                                    bundleType={BundleTypes.BUNDLE_BUILDER}
                                    resourcePickerQuery={buildResourcePickerQuery({ includeUnlisted: true })}
                                    updateKey="review_step.addon_products"
                                    multiple={true}
                                    value={reviewStep?.addon_products}
                                    errorMessage={reviewStepFieldErrors?.addon_products?.message}
                                    onChange={handleProductsChange}
                                />
                                <div style={{ marginTop: "8px" }}>
                                    <Text variant="bodySm" as="p" tone="subdued" alignment="start">
                                        Selected products will appear in the add-on carousel on the review step.
                                    </Text>
                                </div>
                            </div>
                        )}

                        {/* Collection selection */}
                        {reviewStep?.addon_product_selection === "collection" && (
                            <div style={{ display: "flex", flexDirection: "column", marginBottom: 10, width: "100%" }}>
                                <RHFRenderSelectedCollections
                                    updateKey="review_step.collections"
                                    multiple={false}
                                    value={reviewStep?.collections}
                                    errorMessage={reviewStepFieldErrors?.collections?.message}
                                    onChange={handleCollectionsChange}
                                />
                                <div style={{ marginTop: "8px" }}>
                                    <Text variant="bodySm" as="p" tone="subdued" alignment="start">
                                        Products from the selected collection will appear in the add-on carousel on the
                                        review step.
                                    </Text>
                                </div>
                            </div>
                        )}

                        <InlineStack align="space-between" blockAlign="center">
                            <Text as="span" variant="bodyMd" fontWeight="medium">
                                Apply Discounts on Addon Products
                            </Text>
                            <FormToggle
                                name="review_step.apply_discount_on_addon_products"
                                checked={!!reviewStep?.apply_discount_on_addon_products}
                                onChange={(val) => handleEditReviewStep("apply_discount_on_addon_products", val)}
                            />
                        </InlineStack>
                        {!!reviewStep?.apply_discount_on_addon_products && (
                            <div style={{ display: "flex", gap: "15px", marginBottom: "5px" }}>
                                <div style={{ flex: 1 }}>
                                    <FormSelect
                                        name="review_step.discount_type"
                                        label="Discount Type"
                                        value={reviewStep?.discount_type || DISCOUNT_TYPES.PERCENTAGE}
                                        onChange={(val) => handleEditReviewStep("discount_type", val)}
                                        options={[
                                            {
                                                label: `Fixed amount discount ${money_format.split("{{")[0].trim()}`,
                                                value: DISCOUNT_TYPES.FLAT,
                                            },
                                            { label: "Percentage discount %", value: DISCOUNT_TYPES.PERCENTAGE },
                                        ]}
                                    />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <FormTextField
                                        name="review_step.discount_value"
                                        label="Value"
                                        type="number"
                                        value={reviewStep?.discount_value?.toString() || "0"}
                                        onChange={(val) => {
                                            let num = Number(val);
                                            if (reviewStep?.discount_type === DISCOUNT_TYPES.PERCENTAGE) {
                                                num = Math.min(num, 100);
                                            }
                                            num = Math.max(num, 0);
                                            handleEditReviewStep("discount_value", num);
                                        }}
                                        suffix={
                                            getNumberFieldSuffix(
                                                reviewStep?.discount_type || DISCOUNT_TYPES.PERCENTAGE,
                                                money_format,
                                            ).suffix
                                        }
                                    />
                                </div>
                            </div>
                        )}
                    </BlockStack>
                )}
            </BlockStack>
            {/* </Card> */}
        </Box>
    );
};

export default ReviewAddOnProducts;
